/**
 * Online Payments Table Component
 * Displays online payment transactions
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';
import NewFeeReciept from '../../finance/newFeeReciept';

export class OnlinePaymentsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            onlinePayments: [],
            transactions: [],
            flattenedData: [],
            originalTransactions: [],
            isLoading: false,
            isEnableReceipt: false,
            receiptData: []
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();

        // Bind template methods
        this.snoBodyTemplate = this.snoBodyTemplate.bind(this);
        this.transactionIdBodyTemplate = this.transactionIdBodyTemplate.bind(this);
        this.receiptNoBodyTemplate = this.receiptNoBodyTemplate.bind(this);
        this.receiptDateBodyTemplate = this.receiptDateBodyTemplate.bind(this);
        this.paymentModeBodyTemplate = this.paymentModeBodyTemplate.bind(this);
        this.feeTypeBodyTemplate = this.feeTypeBodyTemplate.bind(this);
        this.paidAmountBodyTemplate = this.paidAmountBodyTemplate.bind(this);
        this.totalBodyTemplate = this.totalBodyTemplate.bind(this);
        this.actionsBodyTemplate = this.actionsBodyTemplate.bind(this);
        
        // Bind methods
        this.syncScrollFromData = this.syncScrollFromData.bind(this);
        this.syncScrollFromGrandTotal = this.syncScrollFromGrandTotal.bind(this);
        this.rowClassName = this.rowClassName.bind(this);
    }

    async componentDidMount() {
        console.log('OnlinePaymentsTable component mounted');
        
        // Check if transactionsData is already passed as prop
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            // Use data from props
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            // Data already loaded but empty
            this.setState({ isLoading: false, onlinePayments: [] });
        }
        // If transactionsLoading is true, wait for componentDidUpdate

        // Set up scroll synchronization after a short delay to ensure DOM is ready
        setTimeout(() => {
            this.attachScrollListener();
        }, 100);
    }

    processTransactionsData = (data) => {
        console.log('OnlinePayments - Processing transactions data:', data);

        const transactions = data?.collections;
        console.log('OnlinePayments - Transactions from collections:', transactions);

        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
            // Filter for online payments only
            const onlinePayments = transactions.filter(t =>
                t.paymentMode === 'Online'
            );

            console.log('Filtered online payments:', onlinePayments.length);

            // Transform and flatten the data to show fee types
            const transformedData = this.transformApiDataToTableFormat(onlinePayments);
            const flattened = this.flattenTransactions(transformedData);

            console.log('Transformed data:', transformedData);
            console.log('Flattened data:', flattened);

            this.setState({
                onlinePayments: onlinePayments,
                transactions: transformedData,
                flattenedData: flattened,
                originalTransactions: onlinePayments,
                isLoading: false
            }, () => {
                // Reattach scroll listener after state update
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            console.warn('No valid transactions in collections');
            this.setState({
                onlinePayments: [],
                transactions: [],
                flattenedData: [],
                originalTransactions: [],
                isLoading: false
            });
        }
    }

    attachScrollListener = () => {
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        
        if (dataWrapper && grandTotalWrapper) {
            // Remove existing listeners first to avoid duplicates
            dataWrapper.removeEventListener('scroll', this.syncScrollFromData);
            grandTotalWrapper.removeEventListener('scroll', this.syncScrollFromGrandTotal);
            
            // Add new listeners - bidirectional sync
            dataWrapper.addEventListener('scroll', this.syncScrollFromData);
            grandTotalWrapper.addEventListener('scroll', this.syncScrollFromGrandTotal);
            
            console.log('OnlinePayments: Scroll listeners attached to both tables');
        }
    }

    syncScrollFromData = (e) => {
        const scrollLeft = e.target.scrollLeft;
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper && grandTotalWrapper.scrollLeft !== scrollLeft) {
            grandTotalWrapper.scrollLeft = scrollLeft;
        }
    }

    syncScrollFromGrandTotal = (e) => {
        const scrollLeft = e.target.scrollLeft;
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper && dataWrapper.scrollLeft !== scrollLeft) {
            dataWrapper.scrollLeft = scrollLeft;
        }
    }

    flattenTransactions = (transactionGroups) => {
        const flattened = [];
        let globalSNo = 1;

        transactionGroups.forEach((group, groupIndex) => {
            const feeDetails = group.feeDetails || [];
            const totalRows = feeDetails.length;

            feeDetails.forEach((feeDetail, feeIndex) => {
                flattened.push({
                    sNo: globalSNo,
                    feeDetail: feeDetail,
                    groupIndex: groupIndex,
                    feeIndex: feeIndex,
                    isFirstRow: feeIndex === 0,
                    isLastRow: feeIndex === totalRows - 1,
                    totalRowSpan: totalRows,
                    transactionId: feeDetail.transactionId,
                    receiptNo: feeDetail.receiptNo,
                    uniqueKey: `${globalSNo}-${feeDetail.receiptNo}-${feeIndex}`
                });
            });

            globalSNo++;
        });

        return flattened;
    };

    transformApiDataToTableFormat = (collections) => {
        if (!collections || collections.length === 0) {
            return [];
        }

        const result = [];
        let sNoCounter = 1;

        collections.forEach((collection) => {
            const transactionId = collection.transctionId || '';
            const receiptNo = collection.receiptNo || 'N/A';
            const receiptDate = collection.receiptDate || null;
            const transactionDate = collection.transactionDate || null;
            const paymentMode = collection.paymentMode || 'Online';
            const remarks = collection.remarks || null;
            const totalAmountPaid = collection.amountPaid || 0;

            const transactionFeeDetails = [];

            // Each feePaidDetails item becomes a row in the table
            if (collection.feePaidDetails && collection.feePaidDetails.length > 0) {
                collection.feePaidDetails.forEach((feeDetail) => {
                    // Handle fee type display - don't show "undefined" or empty payment term
                    let feeTypeDisplay = feeDetail.feeType || 'N/A';
                    if (feeDetail.paymentTerm && feeDetail.paymentTerm !== 'undefined') {
                        feeTypeDisplay = `${feeTypeDisplay} - ${feeDetail.paymentTerm}`;
                    }
                    
                    transactionFeeDetails.push({
                        transactionId: transactionId,
                        remarks: remarks,
                        receiptNo: receiptNo,
                        receiptDate: receiptDate,
                        transactionDate: transactionDate,
                        paymentMode: paymentMode,
                        feeType: feeTypeDisplay,
                        paidAmount: feeDetail.amountPaying || 0,
                        total: totalAmountPaid
                    });
                });
            }

            // Only add if there are fee details
            if (transactionFeeDetails.length > 0) {
                result.push({
                    sNo: sNoCounter++,
                    feeDetails: transactionFeeDetails
                });
            }
        });

        return result;
    };

    rowClassName = (rowData) => {
        if (!rowData) return '';
        
        const classes = [];
        
        // Add class for first row of a transaction (shows separator)
        if (rowData.isFirstRow && rowData.sNo > 1) {
            classes.push('transaction-start-row');
        }
        
        // Add class for last row of a transaction
        if (rowData.isLastRow) {
            classes.push('transaction-last-row');
        }
        
        return classes.join(' ');
    };

    async componentDidUpdate(prevProps) {
        // Check if transactionsData prop has changed
        const { transactionsData, transactionsLoading } = this.props;
        
        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            // Loading finished but no data
            this.setState({ 
                isLoading: false, 
                onlinePayments: [], 
                transactions: [], 
                flattenedData: [], 
                originalTransactions: [] 
            });
        }
    }

    componentWillUnmount() {
        // Clean up scroll event listeners
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        
        if (dataWrapper) {
            dataWrapper.removeEventListener('scroll', this.syncScrollFromData);
        }
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScrollFromGrandTotal);
        }
    }

    formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    // Body Template Methods
    snoBodyTemplate(rowData, { rowIndex }) {
        if (!rowData.isFirstRow) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    }

    transactionIdBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>{rowData.feeDetail?.transactionId || '—'}</span>
                {rowData.feeDetail?.remarks && (
                    <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                        Remarks : {rowData.feeDetail.remarks}
                    </span>
                )}
            </div>
        );
    }

    receiptNoBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail?.receiptNo || '—'}</span>;
    }

    receiptDateBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        const date = rowData.feeDetail?.receiptDate || rowData.feeDetail?.transactionDate;
        return <span>{this.formatDate(date)}</span>;
    }

    paymentModeBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail?.paymentMode || 'Online'}</span>;
    }

    feeTypeBodyTemplate(rowData) {
        // Show on every row
        return <span>{rowData.feeDetail?.feeType || '—'}</span>;
    }

    paidAmountBodyTemplate(rowData) {
        // Show on every row
        const amount = rowData.feeDetail?.paidAmount || 0;
        return <span style={{ display: 'block' }}>₹{amount.toFixed(2)}</span>;
    }

    totalBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        const amount = rowData.feeDetail?.total || 0;
        return <span style={{ display: 'block', fontWeight: 600 }}>₹{amount.toFixed(2)}</span>;
    }

    actionsBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <i
                    className="pi pi-eye"
                    style={{
                        fontSize: '1rem',
                        color: '#076EFF',
                        cursor: 'pointer'
                    }}
                    title="View Receipt"
                    onClick={() => {
                        console.log('View receipt clicked for:', rowData);
                        
                        // Find the original transaction data by matching transactionId
                        const { originalTransactions } = this.state;
                        const originalTransaction = originalTransactions.find(
                            t => t.transctionId === rowData.feeDetail.transactionId
                        );
                        
                        if (originalTransaction) {
                            console.log('Original transaction found:', originalTransaction);
                            // Pass the original transaction data directly to the receipt
                            this.setState({
                                isEnableReceipt: true,
                                receiptData: originalTransaction
                            });
                        } else {
                            console.error('Original transaction not found for:', rowData.feeDetail.transactionId);
                        }
                    }}
                />
            </div>
        );
    }

    calculateGrandTotal = () => {
        return this.state.onlinePayments.reduce((sum, payment) => {
            const amount = payment.amountPaid || payment.totalAmount || 0;
            return sum + amount;
        }, 0);
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        console.log('OnlinePaymentsTable rendering with:', {
            flattenedDataCount: flattenedData.length,
            grandTotal: grandTotal
        });

        return (
            <div className="tab-content online-payments-content">
                {/* Note Section */}
                <div className="info-note">
                    <i className="pi pi-info-circle"></i>
                    <span>Note : Transactions which are showing not including of Other fee Collections, School Store Transactions.</span>
                </div>

                {isLoading && <LoadingComponent />}

                {!isLoading && (
                    <div className="online-payments-table-container">
                        <div className="payment-mode-table-wrapper">
                            <div ref={this.dataScrollRef} className="data-table-scroll-container">
                                <DataTable
                                    value={flattenedData}
                                    scrollable
                                    scrollHeight="flex"
                                    className="payment-mode-table"
                                    showGridlines
                                    stripedRows
                                    emptyMessage="No online payments found"
                                    rowClassName={this.rowClassName}
                                >
                                    <Column
                                        header="S.No"
                                        body={this.snoBodyTemplate}
                                        style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}
                                    />
                                    <Column
                                        header="TransactionID"
                                        body={this.transactionIdBodyTemplate}
                                        style={{ width: '250px', minWidth: '250px' }}
                                    />
                                    <Column
                                        header="Receipt No"
                                        body={this.receiptNoBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Receipt Date"
                                        body={this.receiptDateBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Payment Mode"
                                        body={this.paymentModeBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Fee Type"
                                        body={this.feeTypeBodyTemplate}
                                        style={{ width: '280px', minWidth: '280px' }}
                                    />
                                    <Column
                                        header="Paid Amount"
                                        body={this.paidAmountBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Total"
                                        body={this.totalBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={this.actionsBodyTemplate}
                                        style={{ width: '100px', minWidth: '100px', textAlign: 'center' }}
                                    />
                                </DataTable>
                            </div>

                            <div ref={this.grandTotalScrollRef}>
                                <DataTable
                                    value={[{ label: 'Total', amount: grandTotal }]}
                                    className="payment-mode-grand-total-table"
                                    scrollable
                                    scrollHeight="flex"
                                    showGridlines
                                >
                                    <Column body={() => ''} style={{ width: '80px', minWidth: '80px' }} />
                                    <Column body={() => ''} style={{ width: '250px', minWidth: '250px' }} />
                                    <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    <Column body={() => ''} style={{ width: '280px', minWidth: '280px' }} />
                                    <Column 
                                        body={() => <strong style={{ color: '#076EFF' }}>Total</strong>} 
                                        style={{ width: '150px', minWidth: '150px', textAlign: 'left' }} 
                                    />
                                    <Column 
                                        body={(rowData) => <strong style={{ color: '#076EFF' }}>{rowData.amount.toFixed(2)}</strong>} 
                                        style={{ width: '150px', minWidth: '150px', textAlign: 'left' }} 
                                    />
                                    <Column body={() => ''} style={{ width: '100px', minWidth: '100px' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.isEnableReceipt && (
                    <NewFeeReciept
                        receiptData={this.state?.receiptData}
                        hideFeeReciept={() => {
                            this.setState({
                                isEnableReceipt: false,
                                receiptData: []
                            });
                        }}
                    />
                )}
            </div>
        );
    }
}
